import { useState, useRef } from 'react';
import {
    AnswerIcon,
    CollectedKnowledgeSliceDocumentIconSvg,
    CollectedKnowledgeSliceVideoIconSvg,
} from '@/sub-luca-plugin/compatible-components/svg-icons';
import ProjectFilter from './project-filter';
import Skeleton from './skeleton';
import { query_collects } from '@/sub-luca-plugin/apis';
import { IProjectIds, IMyQuestionItem } from '@/sub-luca-plugin/types';
import { navigateExample } from '@/sub-luca-plugin/utils/class-example';
import { ScrollView, Image } from '@/sub-luca-plugin/compatible-components';
import { useNewestData } from '@/sub-luca-plugin/utils/custom-hooks';
import classNames from 'classnames';
import './index.scss';

interface ICloseInfo {
    collectFlag: boolean;
}

interface IProps {
    projectId?: string;
    subjectId?: string;
    twoLevelSubjectId?: string;
    pageSize?: number;
}

const MyCollectRender = (props: IProps) => {
    const projctParams = useRef<any>({
        projectId: props.projectId && +props.projectId,
        subjectId: props.subjectId && +props.subjectId,
        twoLevelSubjectId: props.twoLevelSubjectId && +props.twoLevelSubjectId,
    });
    const pageNum = useRef(1);
    const categories = useRef<Array<{ questionType: 0 | 1; name: string }>>([
        { questionType: 0, name: '问题' },
        { questionType: 1, name: '知识切片' },
    ]);
    const [questionType, setQuestionType] = useState(0);
    const [initLoading, setInitLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [totalNum, setTotal] = useState(0);
    const [list, setList] = useState<IMyQuestionItem[]>([]);
    const [currentCommIndex, setCurrentCommIndex] = useState<number>();
    const [noData, setNoData] = useState(true);

    const newestInitLoading = useNewestData(initLoading);
    const newestLoading = useNewestData(loading);

    // 请求数据列表
    const getList = async (params: {
        projectId?: number;
        subjectId?: number;
        twoLevelSubjectId?: number;
        questionType: 0 | 1;
        pageNum: number;
    }) => {
        params.pageNum === 1 ? setInitLoading(true) : setLoading(true);
        const { result } = await query_collects({ ...params, pageSize: props.pageSize || 20 });
        params.pageNum === 1 ? setInitLoading(false) : setLoading(false);
        const { totalSize = 0, list = [] } = result || {};
        setNoData(params.pageNum === 1 && list.length === 0);
        setTotal(totalSize);
        setList(oldList => {
            const newList = params.pageNum > 1 ? oldList.concat(list) : list;
            return newList;
        });
    };

    // 触底分页
    const handleScrollToLower = () => {
        if (list.length >= totalNum || newestInitLoading.current || newestLoading.current) return;
        pageNum.current += 1;
        getList({ ...projctParams.current, questionType, pageNum: pageNum.current });
    };

    // 项目筛选变更
    const handleProjectChange = (params: IProjectIds) => {
        projctParams.current = params;
        pageNum.current = 1;
        getList({ ...projctParams.current, questionType, pageNum: pageNum.current });
    };

    // 分类变更
    const handleCategoryChange = (type: 0 | 1) => {
        pageNum.current = 1;
        setQuestionType(type);
        setList([]);
        getList({ ...projctParams.current, questionType: type, pageNum: pageNum.current });
    };

    const handleViewQuestionDetail = (data: IMyQuestionItem, index: number) => {
        setCurrentCommIndex(index);
        if (data.myAsk && !data.qaId) {
            navigateExample.linkTo(
                'qa',
                {
                    lucaAskChatId: data.lucaAskChatId,
                    askId: data.askId,
                    showCreateNewQaBtn: false,
                    from: 'my_collect',
                },
                {
                    onClose: (info?: ICloseInfo) => {
                        if (info?.collectFlag === false) {
                            const newList = list.filter(item => item.lucaAskChatId !== data.lucaAskChatId);
                            setList(newList);
                            setTotal(totalNum - 1);
                            newList.length === 0 && setNoData(true);
                        }
                        setCurrentCommIndex(undefined);
                    },
                }
            );
        } else {
            navigateExample.linkTo(
                data.qaId ? 'qa_pair_detail' : 'gbot_detail',
                {
                    id: data.qaId || data.askId,
                    from: 'my_collect',
                },
                {
                    onClose: (info?: ICloseInfo) => {
                        if (info?.collectFlag === false) {
                            const newList = list.filter(item => {
                                if (data.qaId && item.qaId === data.qaId) return false;
                                if (data.askId && item.askId === data.askId) return false;
                                return true;
                            });
                            setList(newList);
                            setTotal(totalNum - 1);
                            newList.length === 0 && setNoData(true);
                        }
                        setCurrentCommIndex(undefined);
                    },
                }
            );
        }
    };

    const handleViewKnowledgeSliceDetail = (data: IMyQuestionItem, index: number) => {
        setCurrentCommIndex(index);
        if (data.resourceType === 0) {
            navigateExample.linkTo(
                'video',
                {
                    title: data.title,
                    videoId: data.resource,
                    recordId: data.recordId,
                    autoplay: true,
                    from: 'my_collect',
                    projectId: data.projectId,
                    subjectId: data.subjectId,
                    twoLevelSubjectId: data.twoLevelSubjectId,
                    fragmentStartTime: data.playStartTime,
                    fragmentEndTime: data.playEndTime,
                },
                {
                    onClose: (info?: ICloseInfo) => {
                        if (info?.collectFlag === false) {
                            const newList = list.filter(item => item.recordId !== data.recordId);
                            setList(newList);
                            setTotal(totalNum - 1);
                            newList.length === 0 && setNoData(true);
                        }
                        setCurrentCommIndex(undefined);
                    },
                }
            );
        } else {
            navigateExample.linkTo(
                'knowledge_points_detail',
                {
                    title: data.title,
                    content: data.resource,
                    recordId: data.recordId,
                    from: 'my_collect',
                    projectId: data.projectId,
                    subjectId: data.subjectId,
                    twoLevelSubjectId: data.twoLevelSubjectId,
                },
                {
                    onClose: (info?: ICloseInfo) => {
                        if (info?.collectFlag === false) {
                            const newList = list.filter(item => item.recordId !== data.recordId);
                            setList(newList);
                            setTotal(totalNum - 1);
                            newList.length === 0 && setNoData(true);
                        }
                        setCurrentCommIndex(undefined);
                    },
                }
            );
        }
    };

    return (
        <div className="my-collect-render">
            <ProjectFilter type="my_collect" initIds={projctParams.current} onChange={handleProjectChange} />
            <div className="collect-categories">
                {categories.current.map(item => (
                    <div
                        key={item.questionType}
                        className={classNames('collect-category', item.questionType === questionType && 'collect-category--selected')}
                        onClick={() => handleCategoryChange(item.questionType)}
                    >
                        {item.name}
                    </div>
                ))}
            </div>
            <div className="my-collect-content__list-container">
                {initLoading && <Skeleton />}
                {!initLoading && noData && (
                    <div className="my-collect-no-data">
                        <img src="http://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202403/12/3a5fd_20240312134539.png" />
                    </div>
                )}
                {!initLoading && !noData && (
                    <ScrollView
                        className="my-collect-content__scroll-container"
                        scrollY
                        onScrollToLower={handleScrollToLower}
                        lowerThreshold={100}
                    >
                        {questionType === 0 ? (
                            <div className="question-list">
                                {list.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className={classNames(
                                            'question-list-item',
                                            index === currentCommIndex && 'question-list-item--selected'
                                        )}
                                        onClick={() => handleViewQuestionDetail(item, index)}
                                    >
                                        <div className="question-list-item__icon-container">
                                            <AnswerIcon />
                                        </div>
                                        <div className="question-list-item__info">
                                            <div className="question-list-item__title">
                                                {item.askChatTitle ? (
                                                    <span>{item.askChatTitle}</span>
                                                ) : (
                                                    <span style={{ color: '#a5a5a5' }}>暂无提问主题</span>
                                                )}
                                            </div>
                                            <div className="question-list-item__other-info">
                                                {item.transferLaborState === 1 && !item.qaId && (
                                                    <div className="question-list-item__teacher-label">名师</div>
                                                )}
                                                <span className="question-list-item__create-time">{item.createTime}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {loading && <div className="my-collect-content__loading">加载中...</div>}
                                {list.length >= totalNum && <div className="my-collect-content__no-more">没有更多了~</div>}
                            </div>
                        ) : (
                            <div className="knowledge-slice">
                                {list.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className={classNames(
                                            'knowledge-slice-item',
                                            index === currentCommIndex && 'knowledge-slice-item--actived'
                                        )}
                                        onClick={() => handleViewKnowledgeSliceDetail(item, index)}
                                    >
                                        <div className="knowledge-slice-cover-contaier">
                                            <div
                                                className={classNames(
                                                    'knowledge-slice-cover',
                                                    item.resourceType === 0 && 'knowledge-slice-cover--video'
                                                )}
                                            >
                                                <Image className="knowledge-slice-cover__img" src={item.coverImageUrl} />
                                            </div>
                                        </div>
                                        <div className="knowledge-slice-describe">
                                            <div className="knowledge-slice-describe-icon-container">
                                                {item.resourceType === 0 && (
                                                    <CollectedKnowledgeSliceVideoIconSvg className="knowledge-slice-describe-icon" />
                                                )}
                                                {item.resourceType === 1 && (
                                                    <CollectedKnowledgeSliceDocumentIconSvg className="knowledge-slice-describe-icon" />
                                                )}
                                            </div>
                                            {item.title}
                                        </div>
                                        <div className="knowledge-slice-createTime">{item.createTime}</div>
                                    </div>
                                ))}
                                {loading && <div className="my-collect-content__loading">加载中...</div>}
                                {list.length >= totalNum && <div className="my-collect-content__no-more">没有更多了~</div>}
                            </div>
                        )}
                    </ScrollView>
                )}
            </div>
        </div>
    );
};

export default MyCollectRender;
